<template>
  <div>
    <Head title="Helfer Anfragen" />
    <h1 class="mb-8 text-xl font-bold">Helfer Anfragen (Pipeline)</h1>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">Name</th>
            <th class="pb-4 pt-6 px-6">E-Mail</th>
            <th class="pb-4 pt-6 px-6">Gewünschte Schicht</th>
            <th class="pb-4 pt-6 px-6">T-Shirt / Essen</th>
            <th class="pb-4 pt-6 px-6">Bemerkungen</th>
            <th class="pb-4 pt-6 px-6 text-right">Aktionen</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="request in requests" :key="request.id" :class="{'bg-green-50': request.status === 'accepted', 'bg-yellow-50': request.status === 'rejected'}" class="hover:bg-gray-100 focus-within:bg-gray-100">
            <td class="border-t px-6 py-4">
              <div class="flex items-center">
                <div v-if="request.status === 'accepted'" class="mr-2 text-green-600" title="Akzeptiert">
                  <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M0 11l2-2 5 5L18 3l2 2L7 18z" /></svg>
                </div>
                <div v-if="request.status === 'rejected'" class="mr-2 text-yellow-600" title="Im Gespräch">
                  <svg class="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4v-4H4a2 2 0 01-2-2V5z" />
                  </svg>
                </div>
                <div>
                  {{ request.name }}<br />
                  <span class="text-xs text-gray-500">{{ request.phone }}</span>
                </div>
              </div>
            </td>
            <td class="border-t px-6 py-4">
              <a :href="`mailto:${request.email}`" class="text-indigo-600 hover:underline">{{ request.email }}</a>
            </td>
            <td class="border-t px-6 py-4">
              <div v-if="request.shift">
                {{ request.shift.ressort_name }}<br />
                <span class="text-xs text-gray-500">{{ request.shift.day }}, {{ formatTime(request.shift.time_start) }} - {{ formatTime(request.shift.time_end) }}</span>
              </div>
              <div v-else-if="request.ressort || request.category">
                <span class="font-medium">{{ request.ressort ? request.ressort.name : request.category.name }}</span><br />
                <span v-if="request.preferred_days" class="text-xs text-gray-500">
                  Tage: {{ request.preferred_days.join(', ') }}
                </span>
              </div>
              <div v-else class="text-gray-400 italic">Keine Präferenz angegeben</div>
            </td>
            <td class="border-t px-6 py-4">
              {{ request.t_shirt_size }}<br />
              <span class="text-xs text-gray-500">{{ formatFood(request.food) }}</span>
            </td>
            <td class="border-t px-6 py-4 max-w-xs truncate">
              <div :title="request.allergies"><strong>Allergien:</strong> {{ request.allergies || '-' }}</div>
              <div :title="request.other"><strong>Sonstiges:</strong> {{ request.other || '-' }}</div>
            </td>
            <td class="border-t px-6 py-4 text-right whitespace-nowrap">
              <div class="flex justify-end items-center space-x-4">
                <a v-if="request.status !== 'accepted'" :href="generateCounterProposalMailto(request)" class="text-gray-500 hover:text-gray-700 transition-colors" title="Gegenvorschlag">
                  <icon name="mail" class="w-6 h-6 fill-current" />
                </a>
                <button v-if="request.status === 'pending'" class="text-yellow-600 hover:text-yellow-700 transition-colors" title="Im Gespräch" @click="markAsInDiscussion(request.id)">
                  <icon name="chat" class="w-6 h-6 fill-current" />
                </button>
                <button v-if="request.status !== 'accepted'" class="text-green-600 hover:text-green-700 transition-colors" title="Akzeptieren" @click="accept(request.id)">
                  <icon name="check-circle" class="w-6 h-6 fill-current" />
                </button>
                <button class="text-red-600 hover:text-red-700 transition-colors" title="Löschen" @click="destroy(request.id)">
                  <icon name="trash" class="w-6 h-6 fill-current" />
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="requests.length === 0">
            <td class="px-6 py-4 border-t" colspan="6">Keine Anfragen gefunden.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'

export default {
  components: {
    Head,
    Icon,
  },
  layout: Layout,
  props: {
    requests: Array,
    counter_proposal_text: String,
  },
  methods: {
    formatTime(value) {
      if (value === null) return ''
      const hours = Math.floor(value)
      const minutes = (value - hours) * 60
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    },
    formatFood(value) {
      if (value === 'Fleisch' || value === 1 || value === '1') return 'Omnivore'
      if (value === 'Vegetarier' || value === 2 || value === '2') return 'Vegetarisch'
      if (value === 'Veganer' || value === 3 || value === '3') return 'Vegan'
      return value || ''
    },
    accept(id) {
      if (confirm('Möchten Sie diesen Helfer akzeptieren? Ein neuer Helfer-Eintrag wird erstellt und der Schicht zugewiesen.')) {
        this.$inertia.post(`/supporter-requests/${id}/accept`)
      }
    },
    markAsInDiscussion(id) {
      if (confirm('Möchten Sie den Status dieser Anfrage auf "Im Gespräch" setzen?')) {
        this.$inertia.post(`/supporter-requests/${id}/markAsInDiscussion`)
      }
    },
    destroy(id) {
      if (confirm('Möchten Sie diese Anfrage unwiderruflich löschen?')) {
        this.$inertia.post(`/supporter-requests/${id}`, { _method: 'delete' })
      }
    },
    generateCounterProposalMailto(request) {
      const subject = encodeURIComponent('Gegenvorschlag für deine Helfer-Anmeldung')

      let bodyText = this.counter_proposal_text

      if (!bodyText) {
        bodyText = 'Hallo [Name],\n\nvielen Dank für dein Interesse am Festivalkult! Deine gewünschte Schicht ([Ressort]) ist leider schon voll oder passt nicht ganz.\n\nKönntest du dir stattdessen eine andere Schicht vorstellen? Wir hätten noch Bedarf bei:\n- [Schicht 1]\n- [Schicht 2]\n\nGib uns bitte kurz Bescheid.\n\nBeste Grüße,\ndas Team vom Festivalkult'
      }

      const ressortName = request.shift ? request.shift.ressort_name : (request.ressort ? request.ressort.name : (request.category ? request.category.name : 'unbekannt'))

      bodyText = bodyText.replace(/\[Name\]/g, request.name)
      bodyText = bodyText.replace(/\[Ressort\]/g, ressortName)

      const body = encodeURIComponent(bodyText)
      return `mailto:${request.email}?subject=${subject}&body=${body}`
    },
  },
}
</script>
